import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Breadcrumb, Button, Drawer } from 'antd';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  MenuOutlined,
  FileTextOutlined,
  AppstoreAddOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  DollarCircleOutlined,
  DownOutlined,
  ToolOutlined,
  SyncOutlined,
  AuditOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import '../styles/MainLayout.css';
import logo from '../assets/logos/leonix_blanco.svg';
import { RoundFiscalia, RoundLogout } from '../assets/svgs';
import { useLogout } from '../hooks';
import { useAtom } from 'jotai';
import { authAtom } from '../state';
import { OnlineInventoryAPI } from '../modules/OnlineInventory/api/onlineInventoryAPI';
import { InventoryViewAPI } from '../modules/InventoryView/api/inventoryViewAPI';
import type { MenuProps } from 'antd';
const { Header, Content } = Layout;

const MainLayout: React.FC = () => {
  const location = useLocation();
  const [authData] = useAtom(authAtom);
  const { mutateAsync } = useLogout();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hasArchivedRecords, setHasArchivedRecords] = useState(false);
  const [hasArchivedTransfer, setHasArchivedTransfer] = useState(false);
  const [isLoadingTransferCheck, setIsLoadingTransferCheck] = useState(true);
  const [menuToShow, setMenuToShow] = useState<number>(0);

  // Para onlineinventoryarchived
  // useEffect(() => {
  //   if (location.pathname === '/onlineinventoryarchived') {
  //     const checkArchivedOnlineInventory = async () => {
  //       try {
  //         const archivedRecords =
  //           await OnlineInventoryAPI.fetchMovementsArchived();
  //         setHasArchivedRecords(archivedRecords.length > 0);
  //       } catch (error) {
  //         console.error(
  //           'Error al verificar registros archivados (online):',
  //           error
  //         );
  //       }
  //     };

  //     checkArchivedOnlineInventory();
  //   }
  // }, [location.pathname]);

  useEffect(() => {
    const checkArchived = async () => {
      try {
        const userId = authData?.user?.id;
        if (!userId) return;

        const queryKey = ['movements', { new_responsible_id: userId, page: 1 }];
        const archivedRecords = await InventoryViewAPI.fetchMovements({
          queryKey,
        });

        if (archivedRecords.data?.length > 0) {
          setHasArchivedTransfer(true);
        }
      } catch (error) {
        console.error('Error al verificar registros archivados:', error);
      } finally {
        setIsLoadingTransferCheck(false);
      }
    };

    checkArchived();
  }, [authData]);

  const userPermissions = authData?.portalPermissions || [];
  const isSuperAdmin = authData?.superadmin;

  const menuMainItems = [
    {
      key: '/one',
      label: (
        <Link
          to="#"
          onClick={() => {
            setMenuToShow(0);
          }}
        >
          Sistema de gestión de recursos
        </Link>
      ),
    },
    {
      key: '/onlineinventory',
      forbiddenRoles: ['webportal-my-inventory'],
      label: (
        <Link
          to="#"
          onClick={() => {
            setMenuToShow(1);
          }}
        >
          Mi Inventario en línea
        </Link>
      ),
    },
    {
      key: '/downloadrfid',
      forbiddenRoles: ['webportal-rfid'],
      label: (
        <a href="/assets/UHF-A8_v1.0.0.apk" download>
          Descargar App RFID
        </a>
      ),
    },
  ].filter(
    (item) =>
      isSuperAdmin ||
      !item.forbiddenRoles ||
      item.forbiddenRoles.length === 0 ||
      item.forbiddenRoles.some((role) => userPermissions.includes(role))
  );

  // Menu Mi Inventario en línea
  const menuOnlineInventory: MenuProps['items'] = [
    {
      key: '/onlineinventory',
      label: <Link to="/onlineinventory">Mi Inventario en línea</Link>,
    },
    // {
    //   key: '/onlineinventoryarchived',
    //   label: (
    //     <Link
    //       to={hasArchivedRecords ? '/onlineinventoryarchived' : '#'}
    //       style={{
    //         color: hasArchivedRecords ? 'inherit' : 'gray',
    //         pointerEvents: hasArchivedRecords ? 'auto' : 'none',
    //       }}
    //     >
    //       Registros archivados
    //     </Link>
    //   ),
    //   disabled: !hasArchivedRecords, // Deshabilita la opción en el menú
    // },
  ];

  // Menu Sistema de gestión de recursos
  const menuGeneral: MenuProps['items'] = [
    {
      key: '/',
      forbiddenRoles: ['webportal-manage'],
      label: (
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          <Link to="/">Mis solicitudes</Link>
        </span>
      ),
    },
    {
      key: '/entryrequests',
      forbiddenRoles: ['webportal-entry-request'],
      label: (
        <span>
          <AppstoreAddOutlined style={{ marginRight: 8 }} />
          <Link to="/entryrequests">Solicitudes de entrada</Link>
        </span>
      ),
    },
    {
      key: '/goodsrequests',
      forbiddenRoles: ['webportal-goods-request', 'webportal-asset-request'],
      label: (
        <span>
          <ShoppingCartOutlined style={{ marginRight: 8 }} />
          <Link to="/goodsrequests">Solicitud de bienes</Link>
        </span>
      ),
    },
    {
      key: '/directgoodsrequests',
      forbiddenRoles: [
        'webportal-direct-consumer-good-request',
        'webportal-direct-consumer-good-request-manage',
      ],
      label: (
        <span>
          <ShopOutlined style={{ marginRight: 8 }} />
          <Link to="/directgoodsrequests">Salida de consumo directa</Link>
        </span>
      ),
    },
    {
      key: '/refunds',
      forbiddenRoles: ['webportal-manage'],
      label: (
        <span>
          <SyncOutlined style={{ marginRight: 8 }} />
          <Link to="/refunds">Solicitud de reintegros</Link>
        </span>
      ),
    },
    {
      key: '/transfers',
      forbiddenRoles: ['webportal-manage'],
      label: (
        <span>
          <DollarCircleOutlined style={{ marginRight: 8 }} />
          <Link to="/transfers">Traslados funcionarios</Link>
        </span>
      ),
    },
    {
      key: '/technicalconcept',
      forbiddenRoles: ['webportal-manage'],
      label: (
        <span>
          <ToolOutlined style={{ marginRight: 8 }} />
          <Link to="/technicalconcept">Solicitudes de conceptos técnicos</Link>
        </span>
      ),
    },
    !isLoadingTransferCheck && hasArchivedTransfer
      ? {
          key: '/inventoryview',
          forbiddenRoles: ['webportal-manage'],
          label: (
            <span>
              <AuditOutlined style={{ marginRight: 8 }} />
              <Link to="/inventoryview">Traslados por recibir</Link>
            </span>
          ),
        }
      : null,
    {
      key: '/approve-request',
      forbiddenRoles: ['webportal-entry', 'webportal-goods'],
      label: (
        <span>
          <AuditOutlined style={{ marginRight: 8 }} />
          <Link to="/approve-request">Solicitudes por aprobar</Link>
        </span>
      ),
    },
    {
      key: '/warehouse-view',
      forbiddenRoles: [
        'webportal-entry',
        'webportal-consumables',
        'webportal-refund',
      ],
      label: (
        <span>
          <AuditOutlined style={{ marginRight: 8 }} />
          <Link to="/warehouse-view">Vista de almacén</Link>
        </span>
      ),
    },
    {
      key: '/approvals-technical-concepts',
      forbiddenRoles: [
        'webportal-technical-concept',
        'webportal-expert-technical-concept',
      ],
      label: (
        <span>
          <AuditOutlined style={{ marginRight: 8 }} />
          <Link to="/approvals-technical-concepts">
            Aprobaciones conceptos técnicos
          </Link>
        </span>
      ),
    },
    {
      key: '/requesed-items',
      forbiddenRoles: ['webportal-consumables'],
      label: (
        <span>
          <OrderedListOutlined style={{ marginRight: 8 }} />
          <Link to="/requesed-items">Elementos solicitados</Link>
        </span>
      ),
    },
  ].filter(
    (item) =>
      item &&
      (isSuperAdmin ||
        !item.forbiddenRoles ||
        item.forbiddenRoles.length === 0 ||
        item.forbiddenRoles.some((role) => userPermissions.includes(role)))
  );

  const menuSystem: MenuProps['items'] = [...menuGeneral].filter(
    (item): item is Exclude<typeof item, false | null | undefined> =>
      Boolean(item)
  );

  const menuMap: Record<number, MenuProps['items']> = {
    0: menuSystem,
    1: menuOnlineInventory,
  };

  const showMenu = menuMap[menuToShow] || [];
  const mainMenuSelectedKey = menuToShow === 0 ? '/one' : '/onlineinventory';

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

  const breadcrumbLabel = useMemo(() => {
    const foundItem = showMenu.find((item) => item?.key === location.pathname);
    if (foundItem && 'label' in foundItem) {
      return foundItem.label;
    }
    return 'Sección';
  }, [showMenu, location.pathname]);

  return (
    <Layout className="layout-container">
      {/* HEADER */}
      <Header className="header-container header-main-container">
        <div className="header-left">
          <img
            src={logo}
            alt="Fiscalía"
            className="header-logo"
            width={150}
            height={50}
          />
        </div>
        <div className="header-center">
          <Menu
            theme="dark"
            mode="horizontal"
            selectedKeys={[mainMenuSelectedKey]}
            items={menuMainItems}
            className="menu-desktop"
          />
        </div>
        <div className="header-right">
          <div className="user-info">
            <h2 className="user-name">
              {authData?.user.name} {authData?.user.last_name}
            </h2>
            <h5 className="user-name">{authData?.role}</h5>
          </div>
          {/* <div className="notification-container"> */}
            {/* <RoundFiscalia
              width={45}
              height={45}
              onClick={() => mutateAsync()}
              cursor={'pointer'}
            /> */}
            {/* <span className="notification-dot"></span> */}
          {/* </div> */}
          <DownOutlined
            style={{
              color: 'white',
              fontSize: '18px',
            }}
          />
          <RoundLogout
            width={35}
            height={35}
            onClick={() => {
              mutateAsync();
            }}
            cursor={'pointer'}
          />
        </div>
      </Header>

      <Header className="header-container header-submain-container">
        <div className="header-center">
          <Menu
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={showMenu}
            className="menu-desktop"
          />
          <Button
            type="text"
            icon={<MenuOutlined />}
            className="menu-hamburger"
            onClick={toggleDrawer}
          />
          <Drawer
            title="Menú"
            placement="right"
            onClose={toggleDrawer}
            open={isDrawerOpen}
          >
            <Menu
              mode="vertical"
              selectedKeys={[location.pathname]}
              items={showMenu}
            />
          </Drawer>
        </div>
      </Header>

      {/* BREADCRUMB */}
      <Content className="content-container">
        <Breadcrumb separator=">" className="breadcrumb-container">
          <Breadcrumb.Item>
            <Link to="/">Inicio</Link>
          </Breadcrumb.Item>
          {location.pathname !== '/' && (
            <Breadcrumb.Item>{breadcrumbLabel}</Breadcrumb.Item>
          )}
        </Breadcrumb>

        <div className="outlet-container">
          <Outlet />
        </div>
      </Content>

      {/* FOOTER */}
      {/* <Footer className="footer-container">©2025</Footer> */}
    </Layout>
  );
};

export default MainLayout;
